/**
 * @fileoverview Prospect details query hook.
 * @description Wraps GET /sales/prospects/{prospectId} in a TanStack query, idle until there's a
 * prospect to show.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useQuery } from '@tanstack/react-query'

import { SALES_CRM_QUERY_KEYS } from '@/features/sales/constants/prospects-query-keys.constants'
import { getProspectDetails } from '@/features/sales/services/prospects.service'

/**
 * @description Fetches one prospect in full, with its ETag.
 * @param {string | null} prospectId - The prospect to fetch, or null to stay idle.
 * @returns {ReturnType<typeof useQuery>} The query (data is the prospect and its ETag).
 */
export function useProspectDetails(prospectId: string | null) {
  return useQuery({
    queryKey: SALES_CRM_QUERY_KEYS.prospect(prospectId ?? ''),
    queryFn: () => getProspectDetails(prospectId ?? ''),
    enabled: prospectId !== null,
  })
}
